import { HeidiVersion } from '@heidi/schema/ml-scribe';

import type { Heidi2WelcomeOutcome } from '../../../data/heidi-2';

export interface Heidi2ActivatedWelcomeGate {
  isHeidi2OnboardingEnabled: boolean;
  /** Launch queue has evaluated, with billing info fetched on this load. */
  isClinicianTrialEligibilityLoaded: boolean;
  /** False during workspace-switch reload. */
  isWorkspaceIdSettled: boolean;
  /** Includes paused + Chronicle. */
  isClinicalRecordingActive: boolean;
  isClinicianTrialModalBlocking: boolean;
  /** `user.is_managed`; unknown while loading counts as managed. */
  isUserManaged: boolean | null | undefined;
  hasTeam: boolean;
  heidiVersion: HeidiVersion | null | undefined;
  /**
   * The viewer's own merged `heidi_ii_access_revoked` — the admin's team
   * settings switch and Individuals table write exactly this row. `undefined`
   * while the entitlements query is unsettled or errored, and that suppresses:
   * an unknown revoke must not open the tour for someone shut out of Heidi II.
   */
  isHeidi2Revoked: boolean | undefined;
  welcomeOutcome: Heidi2WelcomeOutcome;
}

/**
 * Anyone on a team someone else already moved to Heidi II, admin or not, who
 * has not been through the welcome. A member the admin revoked is refused by
 * `isHeidi2Revoked` — `heidiVersion` is believed to downgrade for them too,
 * but that is a backend contract this gate does not get to assume.
 */
export const computeShouldShowHeidi2ActivatedWelcome = (
  gate: Heidi2ActivatedWelcomeGate
): boolean =>
  gate.isHeidi2OnboardingEnabled &&
  gate.isClinicianTrialEligibilityLoaded &&
  gate.isWorkspaceIdSettled &&
  !gate.isClinicalRecordingActive &&
  !gate.isClinicianTrialModalBlocking &&
  gate.isUserManaged === false &&
  gate.hasTeam &&
  gate.heidiVersion === HeidiVersion.Value2 &&
  gate.isHeidi2Revoked === false &&
  gate.welcomeOutcome === 'unseen';
